// frontend/src/pages/DashboardPage.js

import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api';
import KpiCard from '../components/dashboard/KpiCard';
import OffensesLineChart from '../components/dashboard/OffensesLineChart';
import OffensesByDowChart from '../components/dashboard/OffensesByDowChart';
import OffensesByHourChart from '../components/dashboard/OffensesByHourChart';
import OffenseTypePieChart from '../components/dashboard/OffenseTypePieChart';
import DemographicsBarChart from '../components/dashboard/DemographicsBarChart';
import ParishBarChart from '../components/dashboard/ParishBarChart';
import clsx from 'clsx';

function DashboardPage() {
  const [period, setPeriod] = useState('30d');
  const [kpiData, setKpiData] = useState(null);
  const [offensesOverTime, setOffensesOverTime] = useState([]);
  const [offensesByDow, setOffensesByDow] = useState([]);
  const [offensesByHour, setOffensesByHour] = useState([]);
  const [offensesByType, setOffensesByType] = useState([]);
  const [offensesByGender, setOffensesByGender] = useState([]);
  const [offensesByAge, setOffensesByAge] = useState([]);
  const [offensesByParish, setOffensesByParish] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // --- FIX A: This line was missing ---
  const [selectedParish, setSelectedParish] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      // --- FIX B: 'byParishRes' was missing from this list ---
      const [kpiRes, overTimeRes, dowRes, hourRes, byTypeRes, byGenderRes, byAgeRes, byParishRes] = await Promise.all([
        apiClient.get(`/api/dashboard/kpis?period=${period}`),
        apiClient.get(`/api/dashboard/offenses-over-time?period=${period}`),
        apiClient.get(`/api/dashboard/offenses-by-dow?period=${period}`),
        apiClient.get(`/api/dashboard/offenses-by-hour?period=${period}`),
        apiClient.get(`/api/dashboard/offenses-by-type?period=${period}`),
        apiClient.get(`/api/dashboard/offenses-by-gender?period=${period}`),
        apiClient.get(`/api/dashboard/offenses-by-age?period=${period}`),
        apiClient.get(`/api/dashboard/offenses-by-parish?period=${period}`),
      ]);
      setKpiData(kpiRes.data);
      setOffensesOverTime(overTimeRes.data);
      setOffensesByDow(dowRes.data);
      setOffensesByHour(hourRes.data);
      setOffensesByType(byTypeRes.data);
      setOffensesByGender(byGenderRes.data);
      setOffensesByAge(byAgeRes.data);
      setOffensesByParish(byParishRes.data);
    } catch (err) {
      setError('Failed to load dashboard data.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleParishClick = (parishName) => {
    alert(`Filtering by ${parishName} - (Drill-down functionality to be implemented)`);
    setSelectedParish(parishName);
  };

  const TimePeriodButton = ({ value, label }) => (
    <button
      onClick={() => setPeriod(value)}
      className={clsx(
        "px-3 py-1 text-sm font-semibold rounded-md transition-colors",
        period === value ? "bg-gold text-white" : "bg-slate-200 text-slate-700 hover:bg-slate-300"
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-800">COMSTAT Dashboard</h1>
        <div className="flex items-center gap-2">
          <TimePeriodButton value="7d" label="Last 7 Days" />
          <TimePeriodButton value="30d" label="Last 30 Days" />
          <TimePeriodButton value="90d" label="Last 90 Days" />
        </div>
      </div>

      {isLoading && <div className="card text-center p-8">Loading dashboard...</div>}
      {error && <div className="card text-center p-8 text-red-500">{error}</div>}

      {!isLoading && !error && kpiData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <KpiCard title="Total Offenses" data={kpiData.totalOffenses} />
            <KpiCard title="New Juveniles" data={kpiData.newJuveniles} />
            <KpiCard title="Active Probation Cases" data={kpiData.activeCases} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card p-6 lg:col-span-2">
              <h2 className="text-lg font-semibold text-slate-700 mb-4">Offenses Over Time</h2>
              <OffensesLineChart data={offensesOverTime} />
            </div>
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-slate-700 mb-4">Offenses by Day of Week</h2>
              <OffensesByDowChart data={offensesByDow} />
            </div>
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-slate-700 mb-4">Offenses by Hour of Day</h2>
              <OffensesByHourChart data={offensesByHour} />
            </div>
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-slate-700 mb-4">Offenses by Parish</h2>
              <ParishBarChart data={offensesByParish} onParishClick={handleParishClick} />
            </div>
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-slate-700 mb-4">Offenses by Type</h2>
              <OffenseTypePieChart data={offensesByType} />
            </div>
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-slate-700 mb-4">Offenses by Gender</h2>
              <DemographicsBarChart data={offensesByGender} label="gender" title="Offenses by Gender" />
            </div>
            <div className="card p-6 lg:col-span-2">
              <h2 className="text-lg font-bold text-slate-800 mb-4">Offenses by Age Group</h2>
              <DemographicsBarChart data={offensesByAge} label="age_group" title="Offenses by Age Group" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DashboardPage;